import { breadcrumbJsonLd, buildMeta } from "../lib/seo.js";
import PageHero from "../components/PageHero/PageHero.jsx";
import SplitFeature from "../components/SplitFeature/SplitFeature.jsx";
import VideoReel from "../components/VideoReel/VideoReel.jsx";
import SectionHeader from "../components/SectionHeader/SectionHeader.jsx";
import CTABand from "../components/CTABand/CTABand.jsx";

const PAGE = {
  title: "3D Visualization Navigator",
  description:
    "The desktop companion to 3D Visualization: work offline, build animations and export HDTV stills and video from your project model.",
  path: "/123bim/navigator",
};
const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "3D Visualization", path: "/3d-visualization" },
  { name: "Navigator", path: PAGE.path },
];

export function meta() {
  return buildMeta({
    ...PAGE,
    image: "/images/site/123bim/navigator",
    jsonLd: [breadcrumbJsonLd(CRUMBS)],
  });
}

const REEL_VIDEOS = [411945641, 411942965, 411944003, 411944670, 411945992, 412015851];

export default function BimNavigator() {
  return (
    <>
      <PageHero
        eyebrow="Navigator"
        title="The desktop companion, offline"
        lead="Interactively view, analyze and present your civil engineering projects in HDTV quality, no internet connection required."
        image="/images/site/123bim/navigator"
        imageAlt="Navigator desktop application showing a highway model"
        breadcrumbs={CRUMBS}
      />

      <section className="section" aria-labelledby="offline-title">
        <div className="container">
          <SplitFeature
            id="offline-title"
            eyebrow="Offline"
            title="Work offline, present anywhere"
            image="/images/site/123bim/browser"
            imageAlt="3D Visualization interactive viewing and navigation across desktop and web applications"
          >
            <p>
              Navigator quickly piggybacks on existing design data to render a fully interactive 3D
              model that can be shared with project stakeholders and the general public, directly or
              via the cloud with 3D Visualization.
            </p>
            <p>
              Because the model runs locally, a hearing room with no internet connection is no
              obstacle: the presenter navigates the full project model from a laptop the same way
              they would online.
            </p>
            <p>
              Easy integration with existing design data, fun and intuitive navigation and unlimited
              model views make it simple to bring the same project into a hearing, a design review
              or a field visit.
            </p>
          </SplitFeature>
        </div>
      </section>

      <section className="section section-alt" aria-labelledby="animations-title">
        <div className="container">
          <SplitFeature
            id="animations-title"
            eyebrow="Animations"
            title="Animations and drive-throughs"
            image="/images/site/123bim/model"
            imageAlt="A Navigator drive-through camera path over an interchange model"
            reverse
          >
            <p>
              Quickly navigate through the 3D model with simple mouse and keyboard controls. Instant
              rendering shows the project in a high-speed gaming-engine environment instead of a
              slow, pre-rendered walkthrough.
            </p>
            <p>
              Pre-defined viewpoints and camera animations simulate drive-throughs and fly-overs,
              and layer controls switch between existing conditions, design alternatives and
              construction staging on the same camera path.
            </p>
          </SplitFeature>
        </div>
      </section>

      <section className="section" aria-labelledby="export-title">
        <div className="container">
          <SplitFeature
            id="export-title"
            eyebrow="Export"
            title="HDTV stills and video export"
            image="/images/site/123bim/overview"
            imageAlt="An HDTV still exported from Navigator"
          >
            <p>
              Rip as many animations and HDTV still images as the project needs, from any point of
              view in the 3D model, without waiting on a render farm or a separate visualization
              request.
            </p>
            <p>
              The same real-time model that supports live navigation generates images, virtual tours
              and videos, including 360-degree video, on demand and around the clock.
            </p>
          </SplitFeature>
        </div>
      </section>

      <section className="section section-alt" aria-labelledby="videos-title">
        <div className="container">
          <SectionHeader
            id="videos-title"
            eyebrow="See it in action"
            title="What Navigator can do"
          />
          <VideoReel videos={REEL_VIDEOS} label="Navigator videos" />
        </div>
      </section>

      <CTABand
        title="Try Navigator on your project"
        text="Send a project model and get an offline-ready Navigator build to present with."
      />
    </>
  );
}
